/**
 * useMfaVerify — admin + tenant variants for ``POST .../mfa/verify``.
 *
 * Body is XOR — ``{code: "123456"}`` OR ``{recovery_code: "..."}``.
 * Backend validator (``AdminMfaVerifyRequest``) enforces the XOR.
 *
 * Success: ``{access_token, ...}`` envelope. The hook caller is
 * responsible for calling ``loginSuccess(access_token)`` and clearing
 * the mfa_pending slot.
 */
import { useMutation } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { ApiError } from "@/lib/apiError";
import { useAuthStore } from "@/stores/useAuthStore";

export interface MfaVerifyResponse {
  access_token: string;
  token_type: "Bearer";
  expires_in: number;
}

interface VerifyInput {
  code?: string;
  recovery_code?: string;
}

function authHeader(): { Authorization: string } {
  const token = useAuthStore.getState().mfaPendingToken;
  if (!token) {
    throw new Error("mfaPendingToken missing from store");
  }
  return { Authorization: `Bearer ${token}` };
}

export function useAdminMfaVerify() {
  return useMutation<MfaVerifyResponse, ApiError, VerifyInput>({
    mutationFn: async (body) => {
      const resp = await apiClient.post<MfaVerifyResponse>(
        "/admin/auth/login/mfa-verify",
        body,
        { headers: authHeader() },
      );
      return resp.data;
    },
  });
}

export function useTenantMfaVerify(tenantSlug: string) {
  return useMutation<MfaVerifyResponse, ApiError, VerifyInput>({
    mutationFn: async (body) => {
      const resp = await apiClient.post<MfaVerifyResponse>(
        `/api/t/${encodeURIComponent(tenantSlug)}/auth/mfa/verify`,
        body,
        { headers: authHeader() },
      );
      return resp.data;
    },
  });
}

// ── Email-MFA B3 ───────────────────────────────────────────────────────────
//
// Email-code verify + resend. Both are pending-token-only (reuse the same
// in-memory ``mfaPendingToken`` auth header — no token-shape change). Verify
// body is ``{code}`` (no recovery-code XOR — there are no recovery codes for
// the email-code method). Success returns the same session envelope as TOTP;
// the caller wires ``loginSuccess`` + redirect identically.

export interface MfaEmailResendResponse {
  message: string;
  resend_cooldown_seconds: number;
}

/** POST /api/t/{slug}/auth/mfa/email/verify — consume the emailed 6-digit code. */
export function useTenantMfaEmailVerify(tenantSlug: string) {
  return useMutation<MfaVerifyResponse, ApiError, { code: string }>({
    mutationFn: async (body) => {
      const resp = await apiClient.post<MfaVerifyResponse>(
        `/api/t/${encodeURIComponent(tenantSlug)}/auth/mfa/email/verify`,
        body,
        { headers: authHeader() },
      );
      return resp.data;
    },
  });
}

/** POST /api/t/{slug}/auth/mfa/email/resend — re-email a fresh code (45s cooldown). */
export function useTenantMfaEmailResend(tenantSlug: string) {
  return useMutation<MfaEmailResendResponse, ApiError, void>({
    mutationFn: async () => {
      const resp = await apiClient.post<MfaEmailResendResponse>(
        `/api/t/${encodeURIComponent(tenantSlug)}/auth/mfa/email/resend`,
        {},
        { headers: authHeader() },
      );
      return resp.data;
    },
  });
}
